import { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { DEFAULT_FEATURES, WEBINAR_FEATURES } from "../utils/campaignConfig";
import { generateCampaignKey } from "../utils/helpers";
import apiService from "../services/api";
import { TenantContext } from "../contexts/TenantContext";
import type { CampaignConfig, CampaignFeatures, FormattedActiveCampaign, UseCampaignReturn, UseActiveCampaignsReturn } from "../types/campaign";

export const useCampaign = (): UseCampaignReturn => {
  // Support both new URL params (/t/:tenantSlug/dashboard/:campaignKey)
  // and legacy query params (/dashboard?campaign=X)
  const params = useParams<{ tenantSlug?: string; campaignKey?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [dynamicCampaign, setDynamicCampaign] = useState<CampaignConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Try tenant context (may be null if not within TenantProvider)
  const tenantCtx = useContext(TenantContext);
  const tenantSlug = params.tenantSlug || null;
  const tenantId = tenantCtx?.tenantId || null;

  // Prefer URL param, fall back to query param for backward compat
  const campaignKey = params.campaignKey || searchParams.get("campaign");

  // Synthetic "All Campaigns" aggregate — all features ON except webinar & DB query.
  // campaign_id and campaign_name MUST be "__all__" — backend endpoints check this
  // literal value to trigger tenant-scoped aggregation. display_name is the human label.
  // Memoized to keep a stable reference and prevent infinite re-render loops
  // (staticCampaign is a useEffect/useMemo dependency downstream).
  const allCampaignsSynthetic = useMemo<CampaignConfig | null>(() => {
    if (campaignKey !== "__all__") return null;
    return {
      campaign_id: "__all__",
      campaign_name: "__all__",
      ad_campaign_name: "__all__",
      display_name: "All Campaigns",
      description: "Aggregate view across all campaigns",
      webinar_id: null,
      features: {
        showProspectList: true,
        showWebinarAttendees: false,
        showAdMetrics: true,
        showLinkedInAutomation: true,
        showConnectionRequests: true,
        showOutreachMessages: true,
        showMessageResponses: true,
        showEmailMetrics: true,
        showNewsletterData: true,
        showSocialMediaMetrics: true,
        showTwitterMetrics: true,
        showFacebookMetrics: true,
        showDatabaseQuery: false,
        showRealTimeData: true,
        showCSVExport: true,
        showAdvancedAnalytics: true,
      },
    };
  }, [campaignKey]);

  const staticCampaign = allCampaignsSynthetic;

  useEffect(() => {
    const fetchDynamicCampaign = async () => {
      if (!campaignKey || staticCampaign) {
        setDynamicCampaign(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // If within a tenant context, scope to that tenant's campaigns
        let rows: Record<string, unknown>[] = [];
        if (tenantId) {
          const result = await apiService.getTenantCampaigns(tenantId, 200, 0);
          if (result.success) {
            rows = result.rows;
          }
        } else {
          const result = await apiService.getActiveCampaigns();
          if (result.success && result.rows) {
            rows = result.rows;
          }
        }

        const matches = rows
          .filter((campaign) => {
            const cId = String(campaign.title || campaign.campaign_id || campaign.id || "");
            return generateCampaignKey(cId) === campaignKey;
          })
          .sort((a, b) => {
            // Prefer the highest year so getCampaignYearVariants on the backend
            // expands from the most recent title (e.g. _2026 finds both 2025+2026).
            const yearA = String(a.title || a.campaign_id || a.id || "").match(/_(\d{4})$/)?.[1] ?? "0";
            const yearB = String(b.title || b.campaign_id || b.id || "").match(/_(\d{4})$/)?.[1] ?? "0";
            return Number(yearB) - Number(yearA);
          });

        const match = matches[0] ?? null;

        if (match) {
          const cId = String(match.title || match.campaign_id || match.id || "");
          const campaignBase = cId.split(/_(?:Standard|Webinar|Emails|SearchOnly)_/i)[0].toLowerCase();
          const relatedWebinar = rows.find((row) => {
            const title = String(row.title || row.campaign_id || "");
            return /_Webinar_/i.test(title) && title.split(/_Webinar_/i)[0].toLowerCase() === campaignBase;
          });
          const webinarId = typeof match.webinar_id === "string"
            ? match.webinar_id
            : relatedWebinar
              ? String(relatedWebinar.title || relatedWebinar.campaign_id || "")
              : null;
          const featureDefaults = /_Webinar_/i.test(cId) ? WEBINAR_FEATURES : DEFAULT_FEATURES;
          setDynamicCampaign({
            ...match,
            campaign_id: cId,
            campaign_name: cId,
            ad_campaign_name: cId.split("_")[0].toLowerCase(),
            display_name: String(match.name || cId.split("_")[0]).replace(/\s*(Standard|Webinar|Emails)\s*\d{4}$/i, "").trim() || cId.split("_")[0],
            description: String(match.description || `Campaign for ${cId.split("_")[0]}`),
            webinar_id: webinarId,
            features: { ...featureDefaults, showWebinarAttendees: Boolean(webinarId), ...(match.features && typeof match.features === "object" ? match.features : {}) },
            isDynamic: true,
          } as CampaignConfig);
        } else {
          setError("Campaign not found");
        }
      } catch (err) {
        console.error("Error fetching campaign:", err);
        setError((err as Error).message || "Failed to load campaign");
      } finally {
        setLoading(false);
      }
    };

    fetchDynamicCampaign();
  }, [campaignKey, staticCampaign, tenantId]);

  const campaign = useMemo<CampaignConfig | null>(() => {
    return staticCampaign || dynamicCampaign;
  }, [staticCampaign, dynamicCampaign]);

  const features = useMemo<Partial<CampaignFeatures>>(() => {
    return campaign?.features || {};
  }, [campaign]);

  const hasFeature = useCallback(
    (featureKey: string): boolean => {
      if (!campaign) return false;
      return campaign.features?.[featureKey as keyof CampaignFeatures] === true;
    },
    [campaign],
  );

  const campaignId = useMemo<string | null>(() => {
    return campaign?.campaign_id || null;
  }, [campaign]);

  const campaignName = useMemo<string | null>(() => {
    return campaign?.campaign_name || campaign?.campaign_id || null;
  }, [campaign]);

  const adCampaignName = useMemo<string | null>(() => {
    return campaign?.ad_campaign_name || null;
  }, [campaign]);

  const displayName = useMemo<string>(() => {
    return campaign?.display_name || campaignKey || "Campaign";
  }, [campaign, campaignKey]);

  const webinarId = useMemo<string | null>(() => {
    return campaign?.webinar_id || null;
  }, [campaign]);

  const isValid = useMemo<boolean>(() => {
    return !!campaign;
  }, [campaign]);

  const isDynamic = useMemo<boolean>(() => {
    return dynamicCampaign?.isDynamic === true;
  }, [dynamicCampaign]);

  const setCampaign = useCallback(
    (newCampaignKey: string | null) => {
      if (newCampaignKey) {
        // If in tenant context, use path-based navigation
        if (tenantSlug) {
          navigate(`/t/${tenantSlug}/dashboard/${newCampaignKey}`);
        } else {
          setSearchParams({ campaign: newCampaignKey });
        }
      } else {
        if (tenantSlug) {
          navigate(`/t/${tenantSlug}`);
        } else {
          searchParams.delete("campaign");
          setSearchParams(searchParams);
        }
      }
    },
    [tenantSlug, navigate, searchParams, setSearchParams],
  );

  const buildUrl = useCallback(
    (path: string): string => {
      if (tenantSlug && campaignKey) {
        // Map old path names to new route segments
        let segment = path.replace(/^\//, "");
        if (segment === "generated-content") segment = "content";
        return `/t/${tenantSlug}/${segment}/${campaignKey}`;
      }
      // Legacy fallback
      return campaignKey ? `${path}?campaign=${campaignKey}` : path;
    },
    [tenantSlug, campaignKey],
  );

  return {
    campaignKey,
    campaign,
    campaignId,
    campaignName,
    adCampaignName,
    displayName,
    webinarId,
    features,
    loading,
    error,
    isValid,
    isDynamic,
    hasFeature,
    setCampaign,
    buildUrl,
  };
};

export const useActiveCampaigns = (tenantId?: string | null): UseActiveCampaignsReturn => {
  const [campaigns, setCampaigns] = useState<FormattedActiveCampaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      let rows: Record<string, unknown>[] = [];

      if (tenantId) {
        // Tenant-scoped: fetch only this tenant's campaigns
        const result = await apiService.getTenantCampaigns(tenantId, 200, 0);
        if (result.success) {
          rows = result.rows;
        } else {
          throw new Error(result.error || "Failed to load campaigns");
        }
      } else {
        // Legacy: fetch all active campaigns
        const result = await apiService.getActiveCampaigns();
        if (result.success && result.rows) {
          rows = result.rows;
        } else {
          throw new Error(result.error || "Failed to load campaigns");
        }
      }

      const formattedCampaigns: FormattedActiveCampaign[] = rows.map((campaign) => {
        const cId = String(campaign.title || campaign.campaign_id || campaign.id || "");
        const cKey = generateCampaignKey(cId);
        const dName = String(campaign.name || cId.split("_")[0]).replace(/\s*(Standard|Webinar|Emails)\s*\d{4}$/i, "").trim() || cId.split("_")[0];

        return {
          key: cKey,
          name: dName,
          description: String(campaign.description || `Campaign for ${cId.split("_")[0]}`),
          campaign_id: cId,
          campaign_name: cId,
          ad_campaign_name: cId.split("_")[0].toLowerCase(),
          status: campaign.status as string | undefined,
          ...campaign,
        };
      });

      setCampaigns(formattedCampaigns);
    } catch (err) {
      console.error("Error fetching active campaigns:", err);
      setError((err as Error).message || "Failed to load active campaigns");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    campaigns,
    loading,
    error,
    refetch: fetchCampaigns,
  };
};

export default useCampaign;
